import { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

const DISMISS_KEY = "kinntegra_pwa_install_dismissed_v1";

// Detects iOS Safari (where beforeinstallprompt isn't fired)
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};
const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
};

const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS hint — only on iOS Safari, not in PWA mode, not previously dismissed.
    if (isIOS() && !isStandalone() && !localStorage.getItem(DISMISS_KEY)) {
      // Short delay so it doesn't appear on first paint flash.
      const t = setTimeout(() => setShowIosHint(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
    setShowIosHint(false);
  };

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, "1");
    }
    setShow(false);
    setDeferred(null);
  };

  if (!show && !showIosHint) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-50 md:left-auto md:right-4 md:bottom-4 md:max-w-sm bg-white border border-gray-200 rounded-xl shadow-lg p-3 flex items-start gap-3"
      data-testid="pwa-install-prompt"
    >
      <img
        src="/kinntegra-logo.png"
        alt="Kinntegra"
        className="w-10 h-10 rounded-lg object-contain flex-shrink-0"
        style={{ backgroundColor: "#1a3f3a" }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">Install Kinntegra</p>
        {showIosHint ? (
          <p className="text-xs text-gray-600 mt-0.5 leading-snug">
            Tap <Share2 className="inline w-3 h-3 mx-0.5" /> Share, then{" "}
            <span className="font-medium">"Add to Home Screen"</span>.
          </p>
        ) : (
          <p className="text-xs text-gray-600 mt-0.5">
            Get one-tap access to your portfolio.
          </p>
        )}
        {!showIosHint && (
          <button
            onClick={handleInstall}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md text-white"
            style={{ backgroundColor: "#1a3f3a" }}
            data-testid="pwa-install-button"
          >
            <Download className="w-3 h-3" /> Install
          </button>
        )}
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 -m-1 rounded text-gray-400 hover:text-gray-600 flex-shrink-0"
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
